// #351 slice A2, brief §5.4 «Una regola su più righe»: one rule sheet writing
// the same preset on several rows. The parameters the athlete sets in the sheet
// (reps range, steps, sets, deload %) are shared; the anchor (the start load,
// the start effort) stays each row's own, read as the sheet read the first one.
// Keep free of React and any UI import.
import type { Draft, SlotDraft } from "./programEdit.js";
import { slotRefKey, type SlotRef } from "./programProgressionApply.js";
import type { EffortScale } from "./effortScale.js";
import type { ProgressionRule } from "./programProgression.js";
import { PRESET_FIELDS, type PresetId, type PresetValues } from "./programProgressionFields.js";
import { presetOfRule, presetValuesFor, type PresetRef } from "./programProgressionPresets.js";
import { presetRuleOn } from "./programProgressionPresetBase.js";
import type { Recognition } from "./programProgressionRecognize.js";
import type { MaxForProposal } from "./resolveDefaultWeight.js";

/** The fields that say where a row starts. */
const ANCHOR_KEYS: readonly string[] = ["load", "effort", "topEffort"];

/** One target of the sheet: its slot, the rule it follows now, its MAX. */
export type RowTarget = { slot: SlotDraft; rec: Recognition; max?: MaxForProposal | undefined };

const refWithMax = (ref: PresetRef, row: RowTarget): PresetRef => (row.max ? { ...ref, max: row.max } : ref);

/** The sheet's values for `row`: the shared ones, with the anchors the athlete
 *  left as the sheet opened (`opened`) read off the row itself. An anchor the
 *  athlete changed is a choice, so it goes to every row. */
export const valuesForRow = (
  id: PresetId,
  shared: PresetValues,
  opened: PresetValues,
  row: RowTarget,
  scale: EffortScale,
  ref: PresetRef,
): PresetValues => {
  const own = presetValuesFor(id, row.slot, row.rec, scale, refWithMax(ref, row));
  const out: PresetValues = { ...shared };
  for (const fl of PRESET_FIELDS[id]) {
    if (!ANCHOR_KEYS.includes(fl.key) || shared[fl.key] !== opened[fl.key]) continue;
    const mine = own[fl.key];
    if (mine === undefined) continue;
    out[fl.key] = mine;
    // The back-off effort is a drop from the top set: it moves with it.
    const sb = shared.backoffEffort;
    const st = shared.topEffort;
    if (fl.key === "topEffort" && typeof mine === "number" && typeof sb === "number" && typeof st === "number") {
      out.backoffEffort = sb + (mine - st);
    }
  }
  return out;
};

/** The rule of each row, in `rows` order: the first is the sheet's own (its
 *  values are the shared ones); the others take the shared parameters on their
 *  own anchors, and keep the effects of the rule they follow when it is the
 *  same preset. */
export const rulesForRows = (
  id: PresetId,
  shared: PresetValues,
  opened: PresetValues,
  rows: readonly RowTarget[],
  scale: EffortScale,
  ref: PresetRef,
  firstBase?: { rule: ProgressionRule; values: PresetValues },
): ProgressionRule[] =>
  rows.map((row, i) => {
    if (i === 0) return presetRuleOn(id, shared, scale, firstBase);
    const values = valuesForRow(id, shared, opened, row, scale, ref);
    const own = row.rec;
    const base =
      own.kind === "rule" && presetOfRule(own.name, own.rule) === id
        ? { rule: own.rule, values: presetValuesFor(id, row.slot, own, scale, refWithMax(ref, row)) }
        : undefined;
    return presetRuleOn(id, values, scale, base);
  });

/** The rows the sheet offers «Also apply to:»: the same exercise on other
 *  days, then the other exercises, this day first. Never a row already a
 *  target; a row with no sets is left out. */
export const alsoApplyTargets = (
  draft: Draft,
  refs: readonly SlotRef[],
): { sameExercise: SlotRef[]; others: SlotRef[] } => {
  const first = refs[0];
  const ids = new Set(refs.map((r) => draft.sessions[r.day]?.slots[r.slot]?.exerciseId));
  const taken = new Set(refs.map((r) => slotRefKey(r)));
  const sameExercise: SlotRef[] = [];
  const others: SlotRef[] = [];
  draft.sessions.forEach((s, day) =>
    s.slots.forEach((slot, i) => {
      if (taken.has(slotRefKey({ day, slot: i })) || slot.setRows.length === 0) return;
      const same = ids.has(slot.exerciseId);
      if (same && !refs.some((r) => r.day === day)) sameExercise.push({ day, slot: i });
      else others.push({ day, slot: i });
    }),
  );
  // This day first, the others in program order.
  others.sort((a, b) => Number(b.day === first?.day) - Number(a.day === first?.day));
  return { sameExercise, others };
};
